import { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Megaphone, DonutIcon as DocumentIcon, Sparkles, UserCircle, Video, Type, Presentation, Scissors, Layers, ArrowLeft, Loader2 } from "lucide-react";
import axiosInstance from "../../API/axiosInstance";
import { useNavigate } from "react-router-dom";
import GenerateContentModal from "../Ads/GenerateContentModal";
import "./CreateVideoModal.scss";

const CreateVideoModal = ({
  isOpen,
  onClose,
  open,
  onOpenChange,
  initialContentType,
  preselectedContentType,
  initialStep,
  initialBrandId,
  selectedBrandId: propSelectedBrandId,
  selectedBrandName: propSelectedBrandName,
  selectedAudienceId,
  selectedAudienceName,
  setInProcessAdWithReference,
  onContentGenerated,
  referenceCard,
  referenceCardId,
  onHandleBrandSelect,
}) => {
  const modalOpen = isOpen || open;
  const modalClose = onClose || onOpenChange;
  const brandId = initialBrandId || propSelectedBrandId;

  const defaultStep = initialStep || (initialContentType || preselectedContentType ? "form" : "content-type");

  const navigate = useNavigate();

  const normalizeContentType = (type) => {
    if (!type) return "videos";
    if (type === "ads" || type === "image") return "image-ads";
    if (type === "video") return "videos";
    return type;
  };

  const contentType = normalizeContentType(initialContentType || preselectedContentType);
  const [selectedContentType, setSelectedContentType] = useState(contentType);
  const [, setSelectedAudience] = useState(null);
  const [, setContentDescription] = useState("");
  useState(5);
  useState("1:1");
  const [, setModalStep] = useState(initialStep);
  const [selectedVideoType, setSelectedVideoType] = useState(null);

  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(
    localStorage.getItem("selectedBrandId") || ""
  );
  const [selectedBrandName, setSelectedBrandName] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isLoadingBrand, setIsLoadingBrand] = useState(false);

  // First useEffect - handles modal opening state
  useEffect(() => {
    if (modalOpen) {
      setModalStep(defaultStep);
      setSelectedContentType(contentType || "videos");
      setSelectedVideoType(null);

      // Sync brand selection with brandId or localStorage when modal opens
      const brandToUse = brandId || localStorage.getItem("selectedBrandId");
      if (brandToUse) {
        setSelectedBrandId(brandToUse);
      }
    }
  }, [modalOpen, defaultStep, contentType, brandId]);


  // Second useEffect - handles click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBrandDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Third useEffect - fetches brands data
  useEffect(() => {
    const token = localStorage.getItem("token");
    const urlSearchParams = new URLSearchParams(window.location.search);
    const brandId = urlSearchParams.get("brandId");
    const createdBrandName = urlSearchParams.get("brandName");

    const fetchBrands = async () => {
      setIsLoadingBrand(true);
      try {
        const response = await axiosInstance.get(`${process.env.REACT_APP_API}brands`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBrands(response.data.data);

        let brand;
        if (brandId) {
          brand = response.data.data.find((b) => b._id === brandId);
        } else {
          brand = response.data.data.find((b) => b._id === selectedBrandId);
        }

        // fallback to 0 index if no match found
        if (!brand && response.data.data.length > 0) {
          brand = response.data.data[0];
          localStorage.setItem("selectedBrandId", response.data.data[0]._id);
          setSelectedBrandId(response.data.data[0]._id);
        }

        if (brand) {
          const brandDetail = {
            _id: brand._id || "",
            name: brand.brandName || "",
            internalName: brand?.internalName || "",
            description: brand.description || "",
            industry: brand.industry || "",
            colors: brand.brandColors || [],
            tone: brand.brandToneAndVoice || "",
            website: brand.url || "",
            targetMarket: brand.targetMarket || "",
            uniqueValue: brand?.uniqueValue || "",
            mainJob: brand.mainJobOfProduct || "",
            differentiation: brand.differentiation || "",
            howItHelps: brand.howItHelps || "",
            features: brand?.features || "",
            benefits: brand.benefits || "",
          };

          if (brandId) {
            setSelectedBrandId(brandId);
            setSelectedBrandName(createdBrandName || brand.internalName || brand.brandName);
            setSelectedBrand(brandDetail);
            localStorage.setItem("selectedBrandId", brand._id);
            setIsLoadingBrand(false);
            return;
          }

          setSelectedBrand(brandDetail);
          setSelectedBrandName(brand.internalName || brand.brandName || "");
        }
      } catch (err) {
        console.error(err, "err");
      } finally {
        setIsLoadingBrand(false);
      }
    };

    if (modalOpen) {
      fetchBrands();
    }
  }, [modalOpen, selectedBrandId]);

  // Early return moved to the end of component
  if (!modalOpen) return null;

  const contentTypeOptions = [
    {
      id: "videos",
      label: "Video Ads",
      description: "Video content and video advertisements",
      icon: DocumentIcon,
      disabled: !!referenceCardId,
    },
    {
      id: "image-ads",
      label: "Image Ads",
      description: "Social media advertisements and promotional content",
      icon: Megaphone,
    },
    {
      id: "coming-soon",
      label: "More Coming Soon",
      description: "New content types are on the way",
      icon: Sparkles,
      disabled: true,
    },
  ];

  const videoTypeOptions = [
    {
      id: "speaking-character",
      label: "Speaking Character",
      description: "A character speaks your script in a distraction-free scene",
      icon: UserCircle,
    },
    {
      id: "talking-head",
      label: "Talking Head",
      description: "A speaking character over a video or image background",
      icon: Video,
    },
    {
      id: "Multi-Scene",
      label: "Multi-Scene",
      description:
        "A sequence of scenes arranged into a single, continuous video",
      icon: Layers,
      disabled: true,
    },
    {
      id: "video-hook-demo",
      label: "Hook + Demo",
      description: "Quick hook first, then a demo of your product or feature",
      icon: Presentation,
      disabled: true,
    },
    {
      id: "fast-cut",
      label: "Fast Cut",
      description:
        "A rapid sequence of short, energetic clips — usually 5 seconds",
      icon: Scissors,
      disabled: true,
    },
    // {
    //   id: "slideshow",
    //   label: "Slideshow",
    //   description:
    //     "A sequence of images or clips arranged into an engaging, dynamic video",
    //   icon: Layers,
    //   disabled: true,
    // },
    {
      id: "wall-of-text",
      label: "More Coming Soon",
      description:
        "New video content types are on the way",
      icon: Type,
      disabled: true,
    },
  ];

  const handleContentTypeSelect = (typeId) => {
    if (selectedContentType === typeId) {
      setSelectedContentType(null);
      return;
    }
    setSelectedContentType(typeId);
    if (typeId === "videos") {
      setModalStep("video-type");
    } else {
      setModalStep("form");
    }
  };

  const handleVideoTypeSelect = (videoTypeId) => {
    setSelectedVideoType(videoTypeId);
    const navState = { state: { brandId: selectedBrandId } };
    if (videoTypeId === "speaking-character") {
      navigate("/app/speaking-character", navState);
      handleClose();
    } else if (videoTypeId === "talking-head") {
      navigate("/app/talking-head", navState);
      handleClose();
    }
  };

  const handleBackToContentType = () => {
    setModalStep("content-type");
    setSelectedContentType(null);
    setSelectedVideoType(null);
  };



  const handleClose = () => {
    setModalStep("content-type");
    setSelectedContentType(null);
    setSelectedVideoType(null);
    setSelectedAudience(null);
    setContentDescription("");
    modalClose();
  };

  const filteredBrands = brands.filter(
    (brand) =>
      brand?.brandName
        ?.toLowerCase()
        .includes(brandSearchTerm?.toLowerCase()) ||
      brand?.description
        ?.toLowerCase()
        ?.includes(brandSearchTerm?.toLowerCase()) ||
      brand?.industry?.toLowerCase()?.includes(brandSearchTerm?.toLowerCase())
  );

  const handleBrandSelect = async (brandId) => {
    localStorage.setItem("selectedBrandId", brandId);
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API}brands/${brandId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSelectedBrandName(response.data.data.brandName || response.data.data.internalName);
      setSelectedBrandId(response.data.data._id);

      const brandDetail = {
        _id: response.data.data._id,
        name: response.data.data.brandName,
        internalName: response.data.data.internalName,
        description: response.data.data.description,
        industry: response.data.data.industry,
        colors: response.data.data.brandColors,
        tone: response.data.data.brandToneAndVoice,
        website: response.data.data.url,
        targetMarket: response.data.data.targetMarket,
        uniqueValue:
          response.data.data.uniqueValue ||
          "AI-powered automation that learns from your team's workflow",
        mainJob: response.data.data.mainJobOfProduct,
        differentiation: response.data.data.differentiation,
        howItHelps: response.data.data.howItHelps,
        features: response.data.data.features,
        benefits: response.data.data.benefits,
      };

      setSelectedBrand(brandDetail);
      setShowBrandDropdown(false);
      setBrandSearchTerm("");
    } catch (err) {
      setShowBrandDropdown(false);
      setBrandSearchTerm("");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={handleClose}
    >
      <div
        className="create-video-modal w-full max-w-4xl bg-white dark:bg-[#111111] rounded-3xl shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Generate Content
              </h2>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                Create personalized content for your brand and audience
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-xl transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {selectedContentType === "videos" ? (
            <div className="flex items-center gap-4 mt-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Content Type
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                >
                  2
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Details
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 mt-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Content Type
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                >
                  2
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Audience
                </span>
              </div>
              <div className="flex-1 h-px bg-gray-300"></div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                >
                  3
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Details
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Brand Selected Notice */}
          <div
            className="create-video-brand-notice mb-6 p-4 rounded-xl border bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30"
          >
            <div className="create-video-brand-notice__layout">
              <div className="create-video-brand-notice__copy">
                <span
                  className="text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400"
                >
                  Brand Selected
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Content will be generated for:
                </span>
              </div>

              {/* Headless UI Dropdown Menu */}
              <div className="create-video-brand-notice__select relative" ref={dropdownRef}>
                <div className="relative">
                  <button
                    type="button"
                    className="create-video-brand-notice__trigger gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 text-sm font-medium inline-flex items-center justify-between"
                    onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                  >
                    <span className="truncate min-w-0 flex-1 text-left flex items-center gap-2">
                      {isLoadingBrand ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin text-orange-500" />
                          <span className="text-gray-400">Loading brand...</span>
                        </>
                      ) : (
                        selectedBrand?.internalName || selectedBrand?.brandName || "Select a brand"
                      )}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </button>

                  {showBrandDropdown && (
                    <div className="create-video-brand-notice__menu absolute top-full left-0 right-0 mt-1 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] shadow-lg z-50 max-h-80 overflow-hidden">
                      {/* Search input */}
                      <div className="p-3 border-b border-gray-200 dark:border-white/10">
                        <div className="relative">
                          <svg
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <input
                            placeholder="Search brands..."
                            value={brandSearchTerm}
                            onChange={(e) => setBrandSearchTerm(e.target.value)}
                            className="pl-10 h-9 border border-gray-300 dark:border-white/10 bg-white dark:bg-[#111111] text-gray-900 dark:text-white rounded text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-orange-500 w-full"
                          />
                        </div>
                      </div>

                      {/* Brand list */}
                      <div className="max-h-60 overflow-y-auto">
                        {filteredBrands.length > 0 ? (
                          filteredBrands.map((brand, index) => (
                            <button
                              key={index}
                              onClick={() => handleBrandSelect(brand._id)}
                            className="w-full min-w-0 px-4 py-2.5 text-left flex items-center gap-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                            >
                              {selectedBrand?._id === brand._id ? (
                                <span className="text-orange-500">✓</span>
                              ) : (
                                <span className="w-4"></span>
                              )}
                            <span className="min-w-0 flex-1 truncate">{brand?.internalName || brand?.brandName}</span>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                            <div className="text-sm">No brands found</div>
                            <div className="text-xs">
                              Try adjusting your search
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              What would you like to create?
            </h3>
            <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
              Choose the type of content you want to generate
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {contentTypeOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => !option.disabled && handleContentTypeSelect(option.id)}
                  disabled={option.disabled}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${option.disabled
                    ? "border-gray-100 bg-gray-50 dark:border-white/5 dark:bg-white/[0.02] cursor-not-allowed opacity-50"
                    : selectedContentType === option.id
                      ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20 dark:hover:bg-white/10"
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedContentType === option.id
                        ? "bg-orange-500 text-white"
                        : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                        }`}
                    >
                      <option.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold mb-1 text-gray-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedContentType === "image-ads" && (
            <div className="-mx-6 -mb-6">
              <GenerateContentModal
                key={selectedBrandId}
                open={true}
                onOpenChange={modalClose}
                isEmbedded={true}
                selectedBrandId={selectedBrandId}
                selectedBrandName={selectedBrandName}
                selectedAudienceId={selectedAudienceId}
                selectedAudienceName={selectedAudienceName}
                setInProcessAdWithReference={setInProcessAdWithReference}
                onContentGenerated={onContentGenerated}
                referenceCard={referenceCard}
                referenceCardId={referenceCardId}
                onHandleBrandSelect={onHandleBrandSelect || handleBrandSelect}
                onBackToContentTypes={() => setSelectedContentType(null)}
              />
            </div>
          )}

          {selectedContentType === "videos" && (
            <>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                What type of video would you like to create?
              </h3>
              <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
                Choose the style of video content you want to generate
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {videoTypeOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => !option.disabled && handleVideoTypeSelect(option.id)}
                    disabled={option.disabled}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${option.disabled
                      ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10"
                      : selectedVideoType === option.id
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20 dark:hover:bg-white/10"
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${option.disabled
                          ? "bg-gray-200 text-gray-400 dark:bg-white/10 dark:text-gray-500"
                          : selectedVideoType === option.id
                            ? "bg-orange-500 text-white"
                            : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400"
                          }`}
                      >
                        <option.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-semibold mb-1 ${option.disabled ? "text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-white"
                            }`}
                        >
                          {option.label}
                        </div>
                        <div className={`text-xs ${option.disabled ? "text-gray-400 dark:text-gray-500" : "text-gray-600 dark:text-gray-400"
                          }`}>
                          {option.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex max-w-full shrink-0 flex-wrap items-center gap-3">
              {selectedContentType === "videos" && (
                <button
                  onClick={handleBackToContentType}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Content Types
                </button>
              )}
              <button
                onClick={handleClose}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
            <p className="ai-disclaimer min-w-0 flex-1 basis-full sm:basis-64">
              <span className="ai-disclaimer__lead">AI can make mistakes.</span>{" "}
              Review your content before publishing. Claims, personal experiences, and testimonials must be genuine and accurate.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateVideoModal;
